import React, { useState, useEffect, useRef } from 'react';
import TopBar from '../../Components/Games/TopBar.jsx';
import ResultPage from '../../Components/Games/ResultPage.jsx';
import StartGame from '../../Components/Games/StartGame.jsx';
import GameIntroScreen from '../../Components/Games/GameIntroScreen.jsx';
import overlay from '../../assets/img/overlay.png';
import shapeShiftIcon from '../../assets/img/icon-Shape-Shift.png';
import './ShapeShift.css';
import { lifestyleApi } from '../../apiService/api';
import { getUrlParams } from '../../apiService/timespam';
import { useGameBackNavigation } from "../../Components/Games/useGameBackNavigation";


// --- Constants ---
const SHAPE_TYPES = ['circle', 'square', 'triangle', 'diamond', 'star', 'hexagon'];
const COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#a855f7', '#ec4899'];
const SIZES = [0.6, 1];

const ShapeIcon = ({ type, color, size }) => {
    const style = {
        fill: color,
        transform: `scale(${size})`,
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
        transformOrigin: 'center center'
    };

    const getPath = () => {
        switch (type) {
            case 'circle': return <circle cx="50" cy="50" r="45" />;
            case 'square': return <rect x="10" y="10" width="80" height="80" rx="12" />;
            case 'triangle': return <path d="M50 10 L90 85 L10 85 Z" strokeLinejoin="round" strokeWidth="8" stroke={color} />;
            case 'diamond': return <rect x="20" y="20" width="60" height="60" rx="5" transform="rotate(45 50 50)" />;
            case 'star': return <path d="M50 5 L63 35 L95 35 L68 55 L79 85 L50 65 L21 85 L32 55 L5 35 L37 35 Z" strokeLinejoin="round" strokeWidth="5" stroke={color} />;
            case 'hexagon': return <path d="M25 10 L75 10 L95 50 L75 90 L25 90 L5 50 Z" />;
            default: return <circle cx="50" cy="50" r="45" />;
        }
    };

    return (
        <svg viewBox="0 0 100 100" className="shape-icon" style={style} preserveAspectRatio="xMidYMid meet">
            {getPath()}
        </svg>
    );
};

const generateRandomItem = () => ({
    id: Math.random().toString(36).substr(2, 9),
    type: SHAPE_TYPES[Math.floor(Math.random() * SHAPE_TYPES.length)],
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: SIZES[Math.floor(Math.random() * SIZES.length)],
});

const generateLevelItems = (count) => Array.from({ length: count }, generateRandomItem);

const applyChange = (items) => {
    const targetIndex = Math.floor(Math.random() * items.length);
    const original = items[targetIndex];
    const newItem = { ...original };
    const changeType = Math.floor(Math.random() * 3);
    const labels = ["Shape", "Color", "Size"];

    if (changeType === 0) {
        let newType = original.type;
        while (newType === original.type) newType = SHAPE_TYPES[Math.floor(Math.random() * SHAPE_TYPES.length)];
        newItem.type = newType;
    } else if (changeType === 1) {
        let newColor = original.color;
        while (newColor === original.color) newColor = COLORS[Math.floor(Math.random() * COLORS.length)];
        newItem.color = newColor;
    } else {
        newItem.size = original.size === SIZES[0] ? SIZES[1] : SIZES[0];
    }

    const newItems = [...items];
    newItems[targetIndex] = newItem;
    return { modifiedItems: newItems, targetId: original.id, changeLabel: labels[changeType] };
};

const TutorialOverlay = ({ step }) => {
    let message = "";
    let subMessage = "";
    if (step === 'MEMORIZE') { message = "Step 1: MEMORIZE"; subMessage = "Look at the shapes carefully."; }
    else if (step === 'MASK') { message = "Step 2: THE SHIFT"; subMessage = "One object changes property..."; }
    else if (step === 'GUESS') { message = "Step 3: SPOT IT"; subMessage = "Tap the object that looks different!"; }

    return (
        <div className="tutorial-overlay" >
            <div className="t-title">{message}</div>
            <div className="t-sub">{subMessage}</div>
        </div>
    );
};

const ShapeShift = () => {
    useGameBackNavigation();
    const [screen, setScreen] = useState("intro");
    const [gameState, setGameState] = useState('START');
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(60);
    const [level, setLevel] = useState(1);
    const [items, setItems] = useState([]);
    const [targetId, setTargetId] = useState(null);
    const [observeProgress, setObserveProgress] = useState(100);
    const [clickedId, setClickedId] = useState(null);
    const [isTutorial, setIsTutorial] = useState(false);
    const [tutorialStep, setTutorialStep] = useState('MEMORIZE');
    const tutorialTimeoutRef = useRef(null);
    const [bestScore, setBestScore] = useState(() => parseInt(localStorage.getItem('ss_bestScore')) || 0);
    const [correctCount, setCorrectCount] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [currentChangeType, setCurrentChangeType] = useState("");
    const [wrongCount, setWrongCount] = useState(0);
    const [backendCognitiveScore, setBackendCognitiveScore] = useState(null);
    const [fastResponseCount, setFastResponseCount] = useState(0);

    // NEW STATE FOR FEEDBACK
    const [feedback, setFeedback] = useState(null);
    const [cid, setCid] = useState("");
    const [groupName, setGroupName] = useState("");
    const [gameStateApi, setGameStateApi] = useState("initial");
    const [lastInsertedId, setLastInsertedId] = useState(null);
    const [goqiiBest, setGoqiiBest] = useState(0);
    const [backendSkill, setBackendSkill] = useState("");
    const [weightages, setWeightages] = useState({ attemptWeightage: 1, accuracyWeightage: 1, streakWeightage: 1, fastWeightage: 1, scoreWeightage: 1, baseQuestionsAttempt: 0, correctMinQuestion: 1, streakMinQuestion: 1, fastResponseMinQuestion: 1, scorePoints: 1 });
    const [configError, setConfigError] = useState(false);


    const timerRef = useRef(null);
    const observeTimerRef = useRef(null);
    const lastInsertedIdRef = useRef(null);
    const gameOverRef = useRef(false);
    const guessStartTimeRef = useRef(null);


    const fetchGameConfig = () => {
        setConfigError(false);
        const { encryptedId, gid } = getUrlParams();

        lifestyleApi.fetchGameDetails(encryptedId, gid)
            .then(res => {
                if (res.data?.code === 200) {
                    const d = res.data.data;
                    setBestScore(d.bestScore);
                    setGoqiiBest(d.goqiiBest);
                    setWeightages({
                        attemptWeightage: d.attemptWeightage ?? 1,
                        accuracyWeightage: d.accuracyWeightage ?? 1,
                        streakWeightage: d.streakWeightage ?? 1,
                        fastWeightage: d.fastWeightage ?? 1,
                        scoreWeightage: d.scoreWeightage ?? 1,
                        baseQuestionsAttempt: d.baseQuestionsAttempt || 0,
                        correctMinQuestion: d.correctMinQuestion || 1,
                        streakMinQuestion: d.streakMinQuestion || 1,
                        fastResponseMinQuestion: d.fastResponseMinQuestion || 1,
                        scorePoints: d.scorePoints || 1,
                    });
                } else {
                    setConfigError(true);
                }
            })
            .catch(err => {
                console.error("fetchGameDetails failed", err);
                setConfigError(true);
            });
    };

    useEffect(() => {
        fetchGameConfig();
    }, []);

    const handleReplay = () => {
        gameOverRef.current = false;
        clearInterval(timerRef.current);
        clearInterval(observeTimerRef.current);
        if (tutorialTimeoutRef.current) clearTimeout(tutorialTimeoutRef.current);

        setFastResponseCount(0);
        setScore(0);
        setLevel(1);
        setTimeLeft(60);
        setCorrectCount(0);
        setWrongCount(0);
        setStreak(0);
        setBestStreak(0);
        setClickedId(null);
        setTargetId(null);
        setIsTutorial(false);
        setFeedback(null); // Reset feedback
        setScreen("game");
        setGameState('READY');

        setTimeout(() => {
            startRealGame();
        }, 1500);
    };

    const handleStartClick = () => {
        startTutorialSequence();
    };

    const startTutorialSequence = () => {
        setIsTutorial(true);
        setScreen("game");
        setScore(0);
        setGameState('OBSERVE');
        setTutorialStep('MEMORIZE');

        const demoItems = [
            { id: 't1', type: 'circle', color: '#3b82f6', size: 1 },
            { id: 't2', type: 'square', color: '#ef4444', size: 1 }
        ];
        setItems(demoItems);
        setObserveProgress(100);

        tutorialTimeoutRef.current = setTimeout(() => {
            setGameState('MASK');
            setTutorialStep('MASK');

            tutorialTimeoutRef.current = setTimeout(() => {
                const changedItems = [
                    { id: 't1', type: 'circle', color: '#3b82f6', size: 1 },
                    { id: 't2', type: 'square', color: '#22c55e', size: 1 }
                ];
                setItems(changedItems);
                setTargetId('t2');

                tutorialTimeoutRef.current = setTimeout(() => {
                    setGameState('GUESS');
                    setTutorialStep('GUESS');
                }, 1000);
            }, 1500);
        }, 3000);
    };

    const transitionToRealGame = () => {
        if (tutorialTimeoutRef.current) clearTimeout(tutorialTimeoutRef.current);
        setTimeLeft(60);
        setIsTutorial(false);
        setGameState('READY');
        setTimeout(() => {
            startRealGame();
        }, 1500);
    };

    const startRealGame = async () => {
        const { encryptedId, gid } = getUrlParams();

        try {
            const res = await lifestyleApi.initialGameScore(encryptedId, gid);

            if (res.data?.code === 200) {
                const data = res.data.data;

                setCid(data.cid);
                setGroupName(data.groupName);
                setLastInsertedId(data.lastInsertedId);
                lastInsertedIdRef.current = data.lastInsertedId;
                setGameStateApi("initial");

            }
        } catch (err) {
            console.error("initialGameScore failed", err);
        }
        setFastResponseCount(0);
        setScore(0);
        setLevel(1);
        setTimeLeft(60);
        setCorrectCount(0);
        setStreak(0);
        setGameState('OBSERVE');
        startLevel(1);
        startGlobalTimer();
    };

    const startGlobalTimer = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    clearInterval(observeTimerRef.current);
                    if (tutorialTimeoutRef.current) clearTimeout(tutorialTimeoutRef.current);

                    gameOverRef.current = true;   // mark game over
                    setGameState('GAMEOVER');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const startLevel = (currLevel) => {
        if (gameOverRef.current) return;
        setGameState('OBSERVE');
        setClickedId(null);
        setObserveProgress(100);
        const itemCount = 4;
        const observeTime = Math.max(1500, 3000 - (currLevel * 100));
        const newItems = generateLevelItems(itemCount);
        setItems(newItems);

        let elapsed = 0;
        const interval = 50;
        if (observeTimerRef.current) clearInterval(observeTimerRef.current);
        observeTimerRef.current = setInterval(() => {
            elapsed += interval;
            const remaining = Math.max(0, 100 - (elapsed / observeTime) * 100);
            setObserveProgress(remaining);
            if (elapsed >= observeTime) {
                clearInterval(observeTimerRef.current);
                triggerShift(newItems);
            }
        }, interval);
    };

    const triggerShift = (currentItems) => {
        if (gameOverRef.current) return;
        setGameState('MASK');
        setTimeout(() => {
            const { modifiedItems, targetId, changeLabel } = applyChange(currentItems);
            setItems(modifiedItems);
            setTargetId(targetId);
            setCurrentChangeType(changeLabel);
            setTimeout(() => {
                if (!gameOverRef.current) setGameState('GUESS');
                guessStartTimeRef.current = performance.now();
            }, 500);
        }, 350);
    };

    const handleCardClick = (id) => {
        if (gameState !== 'GUESS' || clickedId) return;
        setClickedId(id);
        const correct = id === targetId;

        if (isTutorial) {
            if (correct) {
                setFeedback({ type: 'success', text: 'Correct!' });
                setTimeout(() => { setFeedback(null); transitionToRealGame(); }, 800);
            } else {
                setFeedback({ type: 'error', text: 'Try Again!' });
                setTimeout(() => { setFeedback(null); setClickedId(null); }, 500);
            }
            return;
        }

        if (correct) {
            let fastBonus = 0;
            if (guessStartTimeRef.current) {
                const timeTaken = performance.now() - guessStartTimeRef.current;
                if (timeTaken < 2500) { // 2.5 seconds threshold
                    setFastResponseCount(prev => prev + 1);
                    fastBonus = weightages.fastWeightage * 10;
                }
            }

            setFeedback({ type: 'success', text: 'Correct!' });
            // streak here is the streak count BEFORE this correct answer (n-1 for this attempt "n")
            const streakBonus = weightages.streakWeightage * 10 * streak;
            const gained = weightages.scorePoints + streakBonus + fastBonus;
            const newScore = Math.max(0, score + gained);
            setScore(newScore);
            setCorrectCount(prev => prev + 1);
            setStreak(prev => {
                const ns = prev + 1;
                if (ns > bestStreak) setBestStreak(ns);
                return ns;
            });
            if (newScore > bestScore) {
                setBestScore(newScore);
                localStorage.setItem('ss_bestScore', newScore.toString());
            }
            setTimeout(() => { setFeedback(null); setLevel(prev => prev + 1); startLevel(level + 1); }, 900);
        } else {
            setFeedback({ type: 'error', text: `${currentChangeType} changed!` });
            setWrongCount(prev => prev + 1);
            setStreak(0);
            setScore(prev => Math.max(0, prev - weightages.scorePoints / 2));
            setTimeout(() => { setFeedback(null); startLevel(level); }, 1500);
        }
    };

    useEffect(() => {
        return () => {
            clearInterval(timerRef.current);
            clearInterval(observeTimerRef.current);
            if (tutorialTimeoutRef.current) clearTimeout(tutorialTimeoutRef.current);
        };
    }, []);

    const saveFinalGameScore = async () => {
        const { encryptedId, gid } = getUrlParams();
        const goqiiUserId = encryptedId;
        const totalQuestions = correctCount + wrongCount;

        const payload = {
            goqiiUserId,
            gid,
            totalQuestions,
            correctAnswers: correctCount,
            wrongAnswers: wrongCount,
            longestStreak: bestStreak,
            fastResponse: fastResponseCount,
            score,
            cid,
            lastInsertedId: lastInsertedIdRef.current,
        };

        try {
            const res = await lifestyleApi.saveGameScore(
                payload.goqiiUserId,
                payload.gid,
                payload.totalQuestions,
                payload.correctAnswers,
                payload.wrongAnswers,
                payload.longestStreak,
                payload.fastResponse,
                payload.score,
                payload.cid,
                payload.lastInsertedId
            );

            if (res?.data?.data?.cognitiveScore !== undefined) {
                setBackendCognitiveScore(res.data.data.cognitiveScore);
            }
            if (res?.data?.data?.skill) {
                setBackendSkill(res.data.data.skill);
            }
        } catch (err) {
            console.error("saveGameScore failed", err);
        }
    };

    useEffect(() => {
        if (gameState === 'GAMEOVER') {
            saveFinalGameScore();
        }
    }, [gameState]);
    const getQuote = () => {
        if (backendCognitiveScore >= 90) return "Eagle eyes! Your mind adapts instantly.";
        if (backendCognitiveScore >= 75) return "Great flexibility! You spot changes fast.";
        if (backendCognitiveScore >= 60) return "Good focus. Keep sharpening your perception.";
        if (backendCognitiveScore >= 40) return "Nice effort. Your brain is learning to adapt.";
        return "Every round trains your mind. Try again!";
    };


    return (
        <div className="shape-shift-wrapper">
            {configError && (
                <div className="api-error-overlay">
                    <div className="api-error-card">
                        <div className="api-error-title">Something went wrong</div>
                        <div className="api-error-text">There is a server error to open this game. Please try again after some time.</div>
                        <button className="api-error-retry-btn" onClick={fetchGameConfig}>Retry</button>
                    </div>
                </div>
            )}

            {screen === "game" && gameState !== 'GAMEOVER' && !isTutorial && (
                <TopBar score={score} timeLeft={timeLeft} changeDurationMs={700} timerAlertThreshold={10} />
            )}

            <div className="main-area">
                {screen === "intro" ? (
                    <GameIntroScreen backgroundImage={overlay} gameLogo={shapeShiftIcon} title="Shape Shift" onStart={() => setScreen("start")} />
                ) : gameState === 'START' ? (
                    <StartGame
                        backgroundImage={overlay}
                        // hbTitle="Visual Processing"
                        hbHeading="Cognitive Flexibility"
                        hbDescription="Training your visual scanning skills enhances the brain's ability to detect subtle environmental changes and improves cognitive agility."
                        yourBest={bestScore} goqiiBest={goqiiBest} onStart={handleStartClick}
                    />
                ) : gameState === 'GAMEOVER' ? (
                    <ResultPage
                        visible={true} score={score} best={bestScore} correct={correctCount} wrong={wrongCount}
                        longestStreak={bestStreak} fastResponses={fastResponseCount}
                        cognitiveScore={backendCognitiveScore} domainLabel={backendSkill} quote={getQuote()}
                        onReplay={handleReplay}
                    />
                ) : (
                    <div className={`game-board ${isTutorial ? 'tutorial-bg' : ''}`}>

                        {/* FEEDBACK POPUP */}
                        {feedback && (
                            <div className={`feedback-popup ${feedback.type}`}>
                                {feedback.type === 'error' && <span className="feedback-icon-x">✖</span>}
                                {feedback.text}
                            </div>
                        )}

                        {isTutorial && (
                            <div className="tutorial-top-row">
                                <div className="tutorial-badge">Tutorial</div>
                                <button className="tutorial-skip-btn" onClick={transitionToRealGame}>Skip ⏩</button>
                            </div>
                        )}

                        {gameState === 'READY' && (
                            <div className="ready-overlay"><div className="ready-text">START!</div></div>
                        )}

                        {isTutorial && <TutorialOverlay step={tutorialStep} />}

                        {!isTutorial && gameState !== 'READY' && (
                            <div className="phase-header">

                                <div className="level-indicator">
                                    LEVEL {level.toString().padStart(2, '0')}
                                </div>

                                <div className="phase-title">
                                    {gameState === 'OBSERVE' ? 'MEMORIZE' : gameState === 'MASK' ? 'SHIFTING...' : (
                                        <>Find the <span>{currentChangeType}</span> changed item</>
                                    )}
                                </div>
                                {gameState === 'OBSERVE' && (
                                    <div className="timer-track"><div className="timer-fill" style={{ width: `${observeProgress}%` }}></div></div>
                                )}
                            </div>
                        )}

                        {gameState === 'MASK' && (
                            <div className="mask-overlay"><div className="mask-text">SHIFTING</div></div>
                        )}

                        <div className="shape-grid cols-2" style={{ visibility: gameState === 'MASK' ? 'hidden' : 'visible' }}>
                            {items.map((item, index) => {
                                const isClicked = item.id === clickedId;
                                const isTarget = item.id === targetId;
                                let statusClass = isClicked ? (isTarget ? "correct" : "wrong") : (clickedId && isTarget ? "revealed-correct" : "");
                                return (
                                    <div key={index} className={`shape-card ${statusClass}`} onClick={() => handleCardClick(item.id)}>
                                        <ShapeIcon type={item.type} color={item.color} size={item.size} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShapeShift;